<template>
	<div id="product-view-component" class="router-view" v-if="currentSeries">
		<div class="product-site-banner" :style="{'background-color': currentFamily.color}">
			<span class="product-site-banner__title d-inline-block">{{ currentSeries.title }}</span>
			<span class="product-site-banner__subtitle d-inline-block">{{ currentSeries.shortDescription }}</span>
			<div class="back-to-general">
				<router-link to="/"><i class="fas fa-arrow-left"></i> {{ $t('back_to_general') }}</router-link>
			</div>
			<div v-if="currentSeries.hasStreamVideos" class="stream-videos">
				<a :href="currentSeries.streamLink" target="_blank"><i class="fab fa-youtube"></i> {{ $t('go_to_stream_videos') }}</a>
			</div>
		</div>

		<template v-if="contentLoaded">
			<ul class="nav nav-tabs h6 pl-2 mt-3">
				<li class="d-flex align-items-center pr-3 font-weight-normal">{{ $t('versions') }}</li>
				<li class="nav-item" v-for="(version) in currentSeries.versions" :key="version.id">
					<span class="nav-link d-flex align-items-end" :class="navLinkClasses(version)" @click="setCurrentVersion(version)">
						<span class="version-title">{{ version.title }}</span>
						<span v-if="version === currentVersion" class="text-nowrap version-icons">
							<i class="ml-3" :class="contentsIndicatorClasses('slideshow')" @click="imageLibraryIconClickedHandler('slideshow')" :title="$t('product_version_slideshow')"></i>
							<i class="ml-1" :class="contentsIndicatorClasses('loRes')" @click="imageLibraryIconClickedHandler('loRes')" :title="$t('product_version_lo_res_pictures')"></i>
							<i class="ml-1" :class="contentsIndicatorClasses('hiRes')" @click="imageLibraryIconClickedHandler('hiRes')" :title="$t('product_version_hi_res_pictures')"></i>
							<i class="ml-1" :class="contentsIndicatorClasses('videos')" @click="imageLibraryIconClickedHandler('videos')" :title="$t('product_version_hi_res_pictures')"></i>
							<i v-if="version.documentationLinksList.userCanAddItems" class="add-item far fa-plus-square ml-3 clickable clickable--no-hover text-primary" :title="$t('click_to_add_item')" @click.stop.prevent="addItem(version)"></i>
						</span>
					</span>
				</li>
				<li class="d-flex align-items-center px-2 ml-auto">
						<span class="font-weight-normal">{{ documentFilterTitle }}</span>
						<i class="fa fa-filter text-primary font-size-125pct clickable clickable--no-hover ml-2" @click="documentLinksFilterClickedHandler" :title="$t('click_to_filter_documents')"></i>
						<i class="fas fa-download text-primary font-size-125pct clickable clickable--no-hover ml-3" @click="downloadDocumentsClickedHandler" :title="$t('click_to_select_documents_to_download')" ></i>
				</li>
			</ul>

			<div class="document-links-container">
				<div class="document-links-items-container" ref="dlic" v-if="contentLoaded">
					<DocumentLinks v-if="currentVersion.documentationLinksList"
						:version="currentVersion"
						:documentsFiltered="documentsFilteredAndSorted"
						:clientHeight= "clientHeight"
						:clientWidth= "clientWidth"
						@clickCategoryFilter="clickCategoryFilterHandler"
						@clickCleanFilterLanguage="clickCleanFilterLanguageHandler"
						@clickCleanFilterSubcategory="clickCleanFilterSubcategoryHandler"
						@clickSubcategoryFilter="clickSubcategoryFilterHandler"
						@commentIconClicked="commentIconClickedHandler"
						@copyLinkToClipboard="copyLinkToClipboardHandler"
						@editItemClicked="editItemClickedHandler"
						@favoriteIconClicked="favoriteIconClickedHandler"
						@hook:mounted="documentLinksMountedHandler"
						@itemTitleClicked="itemTitleClickedHandler"
						@showHistoryVersionClicked="showHistoryVersionClickedHandler"
						@sortItemsBy="sortItemsByHandler"></DocumentLinks>
				</div>
				<div class="nothing-to-see-container white-space-pre-line text-center mt-3" v-else>
					<i class="no-content-placeholder fas fa-external-link-alt d-block mt-5"></i>
					{{ $t('nothing_to_see_documentation_links') }}
				</div>
			</div>
		</template>
		<ModalMessage v-else :message="$t('loading')"></ModalMessage>

		<ModalEditDocumentLink v-if="modal === 'editDocumentLink'"
			:item="item"
			:version= "currentVersion"
			@cancelClicked="cancelClickedHandler"
			@okClicked="okClickedHandler"/>
		<ModalEditUserNoteComment v-if="modal === 'editUserNoteComment'"
			:item="item"
			:version= "currentVersion"
			@cancelClicked="cancelClickedHandler"
			@okClicked="okClickedHandler"/>
		<ModalShowVersionHistory v-if="modal === 'showHistoryVersion'"
			:item="item"
			@okClicked="cancelClickedHandler"/>
		<ModalDocumentLinksFilter v-if="modal === 'documentLinksFilter'"
			@cancelClicked="cancelClickedHandler"
			@okClicked="cancelClickedHandler"/>
		<ModalDownloadDocuments v-if="modal === 'downloadDocuments'"
			:documentsFiltered="documentsFiltered"
			:seriesName="currentVersion.series.title"
			:seriesVersion="currentVersion.title"
			@cancelClicked="cancelClickedHandler"
			@okClicked="okClickedHandler"/>
	</div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import DocumentLinks from '../components/DocumentLinks.vue';
import ModalDocumentLinksFilter from '../modals/DocumentLinksFilter.vue';
import ModalDownloadDocuments from '../modals/DownloadDocuments.vue';
import ModalEditDocumentLink from '../modals/EditDocumentLink.vue';
import ModalEditUserNoteComment from '../modals/EditUserNoteComment.vue';
import ModalMessage from '../components/ModalMessage.vue';
import ModalShowVersionHistory from '../modals/ShowVersionHistory.vue';
import { CONSTANTS } from '../resources/constants.js';
import { DocumentationLinkItem } from '../resources/classes.js';
import { utilities } from '../resources/utilities.js';
import { BasePermissionEnum } from '../resources/sp-classes.js';

export default {
	components: {
		DocumentLinks,
		ModalEditDocumentLink,
		ModalEditUserNoteComment,
		ModalMessage,
		ModalShowVersionHistory,
		ModalDocumentLinksFilter,
		ModalDownloadDocuments,
	},

	data() {
		return {
			clientHeight: null,
			clientWidth: '0px',
			currentItem: null,
			currentSeries: null,
			currentUrl: this.$route.params.url,
			currentVersion: null,
			item: null,
			modal: null,
			sortingCriteria: null,
			sortingDirection: null,
		};
	},

	computed: {
		...mapGetters([
			'documentationLanguagesDisplayedTitle',
			'documentationSubcategoriesDisplayedIds',
			'documentationCertificationsDisplayedIds',
			'documentationCertificationsSubcategoriesDisplayed',
		]),

		...mapState([
			'documentationCertifications',
			'documentationDocumentLibraries',
			'stateMachine',
			'series',
		]),

		contentLoaded() {
			return this.currentSeries && this.currentSeries.contentsLoaded && this.currentVersion;
		},

		currentFamily() {
			return this.currentSeries && this.currentSeries.family;
		},

		documentFilterTitle() {
			return this.hasElementsFiltered ? this.$t('showingListFiltered', { number: this.documentsFiltered.length, total: this.currentVersion.documentationLinksList.content.length }) : this.$t('showingListNoFiltered', { number: this.documentsFiltered.length });
		},

		documentsFiltered() {
			// when we edit a document name with wrong prefix, for example change 'unxxxxx' for 'inxxxxx', we need to recheck if user has view permission in this DocumentLibrary
			return this.currentVersion.documentationLinksList.content.filter((item) => {
				let isDisplayed = this.documentationSubcategoriesDisplayedIds.includes(item.subcategoryId)
					&& item.hasSomeLanguageDisplayed(this.documentationLanguagesDisplayedTitle),
					documentationDocumentLibrary = (this.documentationDocumentLibraries.find((ddl) => ddl.internalName(this.stateMachine.testMode) === item.libraryName)),
					userHasViewPermissions = (documentationDocumentLibrary === undefined ? true : documentationDocumentLibrary.hasPermission(BasePermissionEnum.ViewListItems)),
					// feature CMIM-NOM-SABER: don't show category 'other'm only must to show if the filter are selected
					isDisplayedByCertification = (this.hasElementsFilteredByCertifications ? (this.documentationCertificationsDisplayedIds.includes(item.certificationId) || this.documentationCertificationsSubcategoriesDisplayed.includes(item.subcategoryId)) : false);
				return (isDisplayed || isDisplayedByCertification) && userHasViewPermissions;
			});
		},

		documentsFilteredAndSorted() {
			switch (this.sortingCriteria) {
				case 'category':
					return this.documentsFiltered.slice(0).sort(utilities.multiCriteriaSort(['category.title', 'subcategory.title', 'title'], this.sortingDirection));
				case 'subcategory':
					return this.documentsFiltered.slice(0).sort(utilities.multiCriteriaSort(['subcategory.title', 'title'], this.sortingDirection));
				case 'language':
					return this.documentsFiltered.slice(0).sort(utilities.multiCriteriaSort(['language.title', 'category.title', 'subcategory.title', 'title'], this.sortingDirection));
				case 'description':
					return this.documentsFiltered.slice(0).sort(utilities.multiCriteriaSort(['description', 'title'], this.sortingDirection));
				case 'documentModified':
					return this.documentsFiltered.slice(0).sort(utilities.multiCriteriaSort(['documentModified', 'title'], this.sortingDirection));
				case 'title':
				default:
					return this.documentsFiltered.slice(0).sort(utilities.multiCriteriaSort('title', this.sortingDirection));
			}
		},

		hasElementsFiltered() {
			return this.documentsFiltered.length !== this.currentVersion.documentationLinksList.content.length;
		},

		hasElementsFilteredByCertifications() {
			return this.documentationCertifications.some((dc) => dc.isDisplayed);
		},
	},

	methods: {
		...mapMutations([
			'setCurrentFamily',
			'setAllCategoryVisibility',
			'setAllDisplayedLanguagesVisibility',
			'setAllSubcategoryVisibility',
			'setCategoryDisplayed',
			'setSubcategoryVisibility',
			'setUserSettings',
			'updateUserNotes',
			'updateUserNotesSeriesDocumentsLinkList',
			'updateUsageTrackerStorage',
		]),

		...mapActions([
			'getLocalIP',
			'getSeriesContents',
			'saveUserNotesItem',
		]),

		addItem(version) {
			this.currentVersion = version;

			this.currentIndex = this.currentVersion.documentationLinksList.content.length;
			this.item = new DocumentationLinkItem({});
			this.setDialogModal('editDocumentLink');
		},


		calculateComponentHeight() {
			let csr = document.querySelectorAll('[data-automation-id="contentScrollRegion"]'),
				// eslint-disable-next-line dot-notation, prefer-destructuring
				dlic = this.$refs['dlic'],
				elem = dlic,
				bottom = 0,
				csrRect, dlicRect;

			if (csr && csr.length) {
				// eslint-disable-next-line prefer-destructuring
				csr = csr[0];
				csr.scrollTop = 0;
				csrRect = csr.getBoundingClientRect();
				dlicRect = dlic.getBoundingClientRect();

				while (elem && (elem !== csr)) {
					bottom += parseFloat(window.getComputedStyle(elem, null).getPropertyValue('border-bottom').replace('px', '')) + parseFloat(window.getComputedStyle(elem, null).getPropertyValue('margin-bottom').replace('px', '')) + parseFloat(window.getComputedStyle(elem, null).getPropertyValue('padding-bottom').replace('px', ''));
					elem = elem.parentElement;
				}

				this.clientHeight = dlic.style.maxHeight = `${(csrRect.height - (dlicRect.top - csrRect.top) - bottom)}px`;
				this.clientWidth = `${dlic.clientWidth}px`;

				if (this.stateMachine.verboseMode) console.log(`[PRODUCTS] Setting new table's maxHeight to ${dlic.style.maxHeight}`);
			}
		},

		cancelAllModalInfo() {
			this.modal = null;
			this.item = null;
		},

		cancelClickedHandler() {
			this.cancelAllModalInfo();
		},

		clickCleanFilterLanguageHandler() {
			if (this.stateMachine.testMode) {
				this.setAllDisplayedLanguagesVisibility(false);
				this.setUserSettings({ field: CONSTANTS.UserSettings.fieldsKeys.filtersLanguage, array: [] });
			}
		},

		commentIconClickedHandler(version, itemIndex) {
			this.currentVersion = version;
			this.item = this.documentsFilteredAndSorted[itemIndex];
			this.setDialogModal('editUserNoteComment');
		},

		clickCleanFilterSubcategoryHandler() {
			if (this.stateMachine.testMode) {
				this.setAllSubcategoryVisibility(true);
				this.setUserSettings({ field: CONSTANTS.UserSettings.fieldsKeys.filtersSubcategory, array: this.documentationSubcategoriesDisplayedIds });
			}
		},

		clickCategoryFilterHandler(itemIndex) {
			const { category } = this.documentsFilteredAndSorted[itemIndex];
			this.setAllCategoryVisibility({ id: category.id, value: false });
			this.setUserSettings({ field: CONSTANTS.UserSettings.fieldsKeys.filtersSubcategory, array: this.documentationSubcategoriesDisplayedIds });

			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.documentLinksFilter, data: { categoryQuickFilter: true, id: category.id }, localIp });
			});
		},

		clickSubcategoryFilterHandler(itemIndex) {
			const item = this.documentsFilteredAndSorted[itemIndex];
			this.setSubcategoryVisibility({ id: item.subcategoryId, value: false });
			this.setCategoryDisplayed();
			this.setUserSettings({ field: CONSTANTS.UserSettings.fieldsKeys.filtersSubcategory, array: this.documentationSubcategoriesDisplayedIds });
			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.documentLinksFilter, data: { subcategoryQuickFilter: true, id: item.subcategoryId }, localIp });
			});
		},

		contentsIndicatorClasses(pictureLibraryPrefix) {
			let classes = 'far contents-indicator';

			switch (pictureLibraryPrefix) {
				case 'slideshow':
					classes += ' fa-images';
					break;
				case 'loRes':
					classes += ' fa-image contents-indicator--small';
					break;
				case 'hiRes':
					classes += ' fa-image contents-indicator--large';
					break;
				case 'videos':
					classes = 'fas contents-indicator fa-video contents-indicator--large';
					break;
				default:
					break;
			}

			if (this.currentVersionLibraryFromPrefix(pictureLibraryPrefix)) {
				classes += ' clickable clickable--no-hover text-primary';
			} else {
				classes += ' text-secondary';
			}

			return classes;
		},

		copyLinkToClipboardHandler(version, itemIndex) {
			const item = this.documentsFilteredAndSorted[itemIndex];
			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.copyLinkToClipboard, data: { documentName: item.title, seriesId: version.seriesId, versionId: version.id }, localIp });
			});
		},

		currentVersionLibraryFromPrefix(pictureLibraryPrefix) {
			return this.currentVersion[`${pictureLibraryPrefix}PicturesLibrary`];
		},

		documentLinksFilterClickedHandler() {
			this.setDialogModal('documentLinksFilter');
		},

		documentLinksMountedHandler() {
			this.calculateComponentHeight();
		},

		downloadDocumentsClickedHandler() {
			this.setDialogModal('downloadDocuments');
		},

		editItemClickedHandler(version, itemIndex) {
			this.currentVersion = version;
			this.item = this.documentsFilteredAndSorted[itemIndex].clone();
			// We have de documentlist documentsFiltered by category/subcategory, this itemIndex is NOT the index on the original array
			this.currentIndex = this.currentVersion.documentationLinksList.content.findIndex((d) => d.id === this.item.id);
			this.setDialogModal('editDocumentLink');
		},

		favoriteIconClickedHandler(version, itemIndex) {
			const item = this.documentsFilteredAndSorted[itemIndex];
			this.updateUserNotes({ name: item.title, seriesId: version.seriesId, isFavourite: true });

			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.favorite, data: { documentName: item.title, seriesId: version.seriesId, versionId: version.id }, localIp });
			});
			this.saveUserNotesItem().then(() => {
				// we have to update the documents of ALL VERSIONS OF ALL SERIES, because if the list is already loaded, this user's notes are not updated.
				this.series.forEach((s) => s.versions.forEach((v) => {
					if (v.documentationLinksList) {
						v.documentationLinksList.content.forEach((d) => {
							this.updateUserNotesSeriesDocumentsLinkList({ document: d, seriesId: s.id });
						});
					}
				}));
			});
		},

		imageLibraryIconClickedHandler(pictureLibraryPrefix) {
			let pictureLibrary = this.currentVersionLibraryFromPrefix(pictureLibraryPrefix);

			if (pictureLibrary) window.open("https://salicru.sharepoint.com/"+pictureLibrary.siteAbsolutePath(), '_blank').focus();
		},

		itemTitleClickedHandler(version, itemIndex) {
			const item = this.documentsFilteredAndSorted[itemIndex];
			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.openPDF, data: { documentName: item.title, seriesId: version.seriesId, versionId: version.id }, localIp });
			});
		},

		navLinkClasses(version) {
			return (this.currentVersion.title === version.title) ? 'active' : 'clickable clickable--no-hover';
		},

		okClickedHandler() {
			switch (this.modal) {
				case 'editDocumentLink':
					if (this.currentIndex === this.currentVersion.documentationLinksList.content.length) {
						this.currentVersion.documentationLinksList.content.push(this.item);
					} else {
						this.currentVersion.documentationLinksList.content.splice(this.currentIndex, 1, this.item);
					}
					break;
				case 'downloadDocuments':
				default:
					break;
			}
			this.cancelAllModalInfo();
		},

		setCurrentVersion(version) {
			this.currentVersion = version;
			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.openSeries, data: { seriesId: version.seriesId, versionId: version.id }, localIp });
			});
		},

		setDialogModal(newModal) {
			if (newModal) {
				this.modal = newModal;
			} else {
				this.cancelAllModalInfo();
			}
		},

		showHistoryVersionClickedHandler(version, itemIndex) {
			this.item = this.documentsFilteredAndSorted[itemIndex];
			this.setDialogModal('showHistoryVersion');

			this.getLocalIP().then((localIp) => {
				this.updateUsageTrackerStorage({ field: CONSTANTS.UserSettings.fieldsKeys.showHistoryVersion, data: { documentName: this.item.title, seriesId: version.seriesId, versionId: version.id }, localIp });
			});
		},

		sortItemsByHandler(criteria) {
			this.sortingDirection = (criteria === this.sortingCriteria ? -this.sortingDirection : 1);
			this.sortingCriteria = criteria;
		},
	},

	created() {
		if (!this.$route.params.url) {
			this.$router.push('/');
		} else {
			this.currentSeries = this.series.find((s) => s.url === this.currentUrl);
			if (this.currentSeries) {
				document.title = this.currentSeries.title;
				this.setCurrentFamily(this.currentFamily);
				if (this.currentSeries.contentsLoaded) {
					this.setCurrentVersion(this.currentSeries.versions[0]);
				} else {
					this.getSeriesContents({ series: this.currentSeries }).then(() => {
						this.setCurrentVersion(this.currentSeries.versions[0]);
					}).catch((error) => console.error('[PRODUCTS] Error at ProductView.created.getSeriesContents', error));
				}
			} else {
				if (this.stateMachine.verboseMode) console.log('[PRODUCTS] ProductView.created(): Back to root');
				this.$router.push('/');
			}
		}
	},
};
</script>

<style lang="scss">
	@font-face {
		font-family: 'Univers LT Std BoldCn';
		src: url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-BoldCn.woff2')
				format('woff2'),
			url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-BoldCn.woff')
				format('woff');
		font-weight: bold;
		font-style: normal;
	}

	@font-face {
		font-family: 'Univers LT Std Cn';
		src: url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-Cn.woff2')
				format('woff2'),
			url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-Cn.woff')
				format('woff');
		font-weight: normal;
		font-style: normal;
	}
	$shadow-color: #666;

	.document-preview-tippy-tooltip-iframe {
		height: calc(var(--height) * 0.9);
		//why use Min in capital?? reason in stackoverflow,
		//https://stackoverflow.com/questions/62280339/css-min-function-to-sass#:~:text=When%20using%20SASS%2C%20min(),%22M%22%20in%20Min().
		width: Min(calc(var(--width) * 0.9), calc(var(--height) * 0.9 * 1.5));
	}

	.back-to-general {
		position: absolute;
			right: 8px;
			top: 8px;
		z-index: 1;

		a {
			color: #fff !important;
			&:hover {
				color: #fff !important;
				text-decoration: none;
			}
		}
	}

	.contents-indicator {
		color: #333;
		font-size: 14px;
		line-height: 19px;
		&.contents-indicator--small {
			font-size: 12px;
		}
		&.contents-indicator--large {
			font-size: 16px;
		}
	}

	.document-links-container {
		position: relative;
		z-index: 1;
	}

	.document-links-items-container {
		overflow-y: auto;
		position: relative;
	}

	.nothing-to-see-container {
		line-height: 20px;
	}

	.product-site-banner {
		border-radius: 0.25rem;
		box-shadow: 2px 2px 3px $shadow-color;
		color: #fff;
		display: block;
		padding: 0.5rem 1rem;
		position: relative;
	}

	.product-site-banner__title {
		font-family: 'Univers LT Std BoldCn';
		font-size: 60px;
		margin-right: 10px;
		line-height: 75px;
		text-shadow: -1px -1px 2px $shadow-color;
	}

	.product-site-banner__subtitle {
		font-family: 'Univers LT Std Cn';
		font-size: 24px;
		letter-spacing: -1.2px;
	}

	.stream-videos {
		position: absolute;
			right: 8px;
			bottom: 8px;
		z-index: 1;
		a {
			color: #fff;
			&:hover {
				color: #fff;
				text-decoration: none;
			}
			i {
				font-size: 1rem;
				position: relative;
					top: 1px;
			}
		}
	}

	.version-icons,
	.version-title {
		height: 19px;
		line-height: 19px;
	}
</style>
